import { useState, useCallback, useRef, useEffect } from 'react'
import { loadDraftFromServer, saveDraftToServer, processFormData } from '../screens/po/components/DraftService'

/**
 * Hook personnalisé pour gérer les brouillons de formulaire
 * @param {Object} app - Contexte de l'application contenant les informations utilisateur
 * @returns {Object} Fonctions et états pour gérer les brouillons
 */
export function useDraftManager(app) {
  // États pour suivre le brouillon, le chargement et les erreurs
  const [draft, setDraft] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [processedData, setProcessedData] = useState({})
  
  // Références pour la file d'attente de sauvegarde
  const saveQueue = useRef([])
  const isSaving = useRef(false)
  const retryTimeouts = useRef([])

  // Nettoyer les timeouts lors du démontage du composant
  useEffect(() => {
    return () => {
      retryTimeouts.current.forEach(timeout => clearTimeout(timeout))
    }
  }, [])

  /**
   * Charge un brouillon depuis le serveur
   * @param {string} draftId - ID du brouillon à charger
   * @returns {Promise<Object|boolean>} Le brouillon chargé ou false en cas d'échec
   */
  const loadDraft = useCallback(async (draftId) => {
    if (!draftId) {
      setError('ID de brouillon manquant')
      return false
    }
    
    setIsLoading(true)
    setError(null)
    
    try {
      // Charger le brouillon depuis le serveur
      const result = await loadDraftFromServer(draftId, app.api_url)
      
      // Mettre à jour l'état avec le brouillon chargé
      setDraft(result)
      
      // Traiter les données du formulaire
      const processed = processFormData(result.form_data)
      setProcessedData(processed)
      
      return result
    } catch (err) {
      // Gérer l'erreur et la rendre disponible pour l'interface utilisateur
      console.error('Erreur lors du chargement du brouillon:', err)
      setError(err.message || 'Erreur lors du chargement du brouillon')
      return false
    } finally {
      setIsLoading(false)
    }
  }, [app.api_url])

  /**
   * Traite la file d'attente de sauvegarde
   * @returns {Promise<string|null>} L'ID du brouillon sauvegardé ou null en cas d'échec
   */
  const processSaveQueue = async () => {
    // Vérifier si la file est vide ou si une sauvegarde est déjà en cours
    if (saveQueue.current.length === 0 || isSaving.current) return null
    
    // Marquer comme en cours de sauvegarde
    isSaving.current = true
    
    // Récupérer la prochaine opération de sauvegarde
    const { formData, step, formType, draftId } = saveQueue.current.shift()
    
    try {
      // Sauvegarder le brouillon sur le serveur
      const newDraftId = await saveDraftToServer(
        formData, 
        step, 
        app, 
        draftId, 
        formType.aidetype, 
        formType.mesure, 
        formType.title
      )
      
      // Mettre à jour l'état avec le nouvel ID
      if (newDraftId) {
        setDraft(prev => {
          const updated = {...prev, draft_id: newDraftId}
          // Ne pas mettre à jour l'étape courante ici pour éviter la boucle infinie
          // avec le composant PoForm
          return updated
        })
        
        // Effacer les erreurs précédentes
        setError(null)
        
        return newDraftId
      }
      
      throw new Error('Erreur lors de la sauvegarde: ID de brouillon non reçu')
    } catch (err) {
      console.error('Erreur lors de la sauvegarde du brouillon:', err)
      setError(err.message || 'Erreur lors de la sauvegarde du brouillon')
      
      // Remettre l'opération dans la file si c'est une erreur réseau
      if (err.message && (err.message.includes('network') || err.message.includes('timeout'))) {
        const timeoutId = setTimeout(() => {
          saveQueue.current.push({ formData, step, formType, draftId })
          isSaving.current = false
          processSaveQueue()
        }, 5000) // Réessayer après 5 secondes
        
        retryTimeouts.current.push(timeoutId)
      }
      
      return null
    } finally {
      // Marquer comme terminé et traiter l'opération suivante si elle existe
      isSaving.current = false
      
      if (saveQueue.current.length > 0) {
        processSaveQueue()
      }
    }
  }

  /**
   * Sauvegarde un brouillon
   * @param {Object} formData - Données du formulaire à sauvegarder
   * @param {number} step - Étape courante du formulaire
   * @param {Object} formType - Type de formulaire (aidetype, mesure, title)
   * @param {string} draftId - ID du brouillon existant (si mise à jour)
   * @returns {Promise<string|null>} L'ID du brouillon sauvegardé ou null
   */
  const saveDraft = useCallback(async (formData, step, formType, draftId) => {
    if (!formData) {
      setError('Données du formulaire manquantes')
      return null
    }
    
    if (!app || !app.profil || !app.profil.id) {
      setError('Informations utilisateur manquantes')
      return null
    }
    
    // Ajouter l'opération à la file d'attente
    const saveOperation = { formData, step, formType, draftId }
    saveQueue.current.push(saveOperation)
    
    // Traiter la file d'attente si aucune sauvegarde n'est en cours
    if (!isSaving.current) {
      return processSaveQueue()
    }
    
    return null
  }, [app])

  /**
   * Récupère les données traitées du brouillon courant
   * @returns {Object} Les données traitées du formulaire
   */
  const getProcessedFormData = useCallback(() => {
    // Utiliser les données déjà traitées si disponibles
    if (Object.keys(processedData).length > 0) {
      return processedData
    }
    
    // Sinon, traiter les données du brouillon
    if (draft && draft.form_data) {
      const processed = processFormData(draft.form_data)
      setProcessedData(processed)
      return processed
    }
    
    return {}
  }, [draft, processedData])

  return {
    draft,
    isLoading,
    error,
    loadDraft,
    saveDraft,
    getProcessedFormData,
    isSaving: isSaving.current
  }
}
